/**
 * Detailed Person Search Filters (Global Search)
 *
 * Overview:
 * - Presents a filter type selector and a dynamic input generator (text/form-controls/select/date).
 * - Builds normalized filter items and delegates add/clear/form-controls/search actions upstream.
 * - Fetches dropdown datasets via Redux (sites, identifications, qualifications, tags, license types).
 * - Shows a loading spinner while dependent lists are being retrieved.
 * - Integrates Ant Design components for layout and UX.
 *
 * Primary responsibilities:
 * - Allow users to select a filter category and provide corresponding values.
 * - Validate filter input before enabling the "Add Filter" action.
 * - Expose actions to clear filters and execute the search.
 *
 * Wrapper component: Filters
 *
 * Orchestrates the filter selection UI for the Detailed Person Search flow,
 * wiring dropdown data, validation, and actions to the parent container.
 *
 * @param {Object} props - Component props
 * @param {Function} props.searchPerson - Triggers the person search in parent
 * @param {Function} props.clearDetailFilter - Clears all applied filters in parent
 * @param {Function} props.handleFilterChange - Appends a new filter to parent state
 * @param {Array<Object>} props.detailFilter - Current filter chips for button enablement
 * @returns {JSX.Element} Filters panel for person search
 */

import { Alert, Col, Row, Space, Spin, Tooltip, Typography } from "antd";
import Button from "@ui/form-controls/button";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import Select from "@ui/form-controls/select";
import FieldTypeGenerator from "../shared/field-type-generator";

import {
  CANDIDATE_STATUS,
  FIELD_TYPES,
  MULTI_SELECT_FILTER_IDS,
  MULTI_TEXT_FILTER_IDS,
  trueFalseOptions
} from "../shared/constants";
import { FILTER_OPTIONS } from "./constants";

import { detailPersonSearchClear } from "@redux/actionCreators";

const { Title } = Typography;

const { TEXT_FIELD, SELECT_FIELD, SELECT_DATE_FIELD, DATE_FIELD } = FIELD_TYPES;

const defaultSelectedField = {
  selectedFieldId: null, // e.g., "ClientInduction"
  selectedFieldName: null, // e.g., "Client Induction"
  selectedFieldType: null, // e.g., "text", "input-select"
  selectedFieldOptions: [], // e.g., [{ Name: <selectedFieldName>, ObjectId: <selectedFieldId/FieldType> }, ...]
  selectedFieldDropDownValue: null,
  selectedFieldTextValue: null,
  selectedFieldDateValue: null
};

const Filters = ({ searchPerson, clearDetailFilter, handleFilterChange, detailFilter }) => {
  const dispatch = useDispatch();

  const [selectKey, setSelectKey] = useState(0);
  const [selectedFilterField, setSelectedFilterField] = useState(defaultSelectedField);

  const driverReducer = useSelector(state => state.driverReducer || null, shallowEqual);
  const candidateReducer = useSelector(state => state.candidateReducer || null, shallowEqual);

  const {
    getSiteList: { resultData: getSiteListData, isLoading: getSiteListLoading }
  } = candidateReducer;

  const {
    getIdentificationNameList: {
      resultData: getIdentificationNameListData,
      isLoading: getIdentificationNameListLoading
    },
    getQualificationTitleList: {
      resultData: getQualificationTitleListData,
      isLoading: getQualificationTitleListLoading
    },
    tagsListInfo: { resultData: tagsList, isLoading: tagsListLoading },
    drivingLicenseCapabilityTypesList: {
      resultData: drivingLicenseCapabilityTypesListData,
      isLoading: drivingLicenseCapabilityTypesListLoading
    }
  } = driverReducer;

  const siteListOptions = useMemo(() => {
    return getSiteListData?.map(({ Name }) => ({
      Name: Name,
      ObjectId: Name
    }));
  }, [getSiteListData]);

  const drivingLIcenseCapabilityTypesOptions = useMemo(() => {
    return drivingLicenseCapabilityTypesListData?.map(({ Title }) => ({
      Name: Title,
      ObjectId: Title
    }));
  }, [drivingLicenseCapabilityTypesListData]);

  const qualificationTitleListOptions = useMemo(() => {
    return getQualificationTitleListData?.map(({ Title }) => ({
      Name: Title,
      ObjectId: Title
    }));
  }, [getQualificationTitleListData]);

  const tagsListOptions = useMemo(() => {
    return tagsList?.Data?.map(({ TagName }) => ({
      Name: TagName,
      ObjectId: TagName
    }));
  }, [tagsList]);

  const getFieldNameById = fieldId => {
    const field = FILTER_OPTIONS.find(option => option.ObjectId.startsWith(`${fieldId}/`));
    return field ? field.Name : "";
  };

  // Handle filter type selection and prepare field options/values
  const handleFilter = filter => {
    if (!filter) {
      setSelectedFilterField(defaultSelectedField);
      return;
    }

    const fieldDropDownOptions = {
      ClientInduction: siteListOptions,
      DrivingLicenceCapability: drivingLIcenseCapabilityTypesOptions,
      DuplicateContacts: trueFalseOptions,
      Identification: getIdentificationNameListData,
      NotClientBanned: siteListOptions,
      Qualification: qualificationTitleListOptions,
      Tags: tagsListOptions,
      WorkedAtSites: siteListOptions,
      SiteOfInterest: siteListOptions,
      Status: CANDIDATE_STATUS,
      STS: trueFalseOptions
    };

    const [fieldId, fieldType] = filter.split("/");

    const filterField = {
      selectedFieldId: fieldId,
      selectedFieldName: getFieldNameById(fieldId),
      selectedFieldType: fieldType
    };

    if (fieldType === SELECT_DATE_FIELD) {
      setSelectedFilterField({
        ...filterField,
        selectedFieldOptions: fieldDropDownOptions[fieldId] || null,
        selectedFieldDateValue: dayjs()
      });
    } else if (fieldType === SELECT_FIELD) {
      setSelectedFilterField({
        ...filterField,
        selectedFieldOptions: fieldDropDownOptions[fieldId] || null
      });
    } else if (fieldType === TEXT_FIELD) {
      setSelectedFilterField({
        ...filterField,
        selectedFieldType: fieldType,
        selectedFieldTextValue: MULTI_TEXT_FILTER_IDS.includes(fieldId) ? [""] : null
      });
    }
  };

  // Option values can be ids (e.g. Identification), so map them back to names for the chip.
  const getDisplayValue = dropDownValue => {
    if (!dropDownValue) return dropDownValue;
    const options = selectedFilterField.selectedFieldOptions || [];
    const toName = value =>
      options.find(option => (option.ObjectId || option.Name) === value)?.Name ?? value;
    return Array.isArray(dropDownValue)
      ? dropDownValue.map(toName).join(", ")
      : toName(dropDownValue);
  };

  const addFilter = () => {
    const rawTextValue = selectedFilterField.selectedFieldTextValue;
    const textValue = Array.isArray(rawTextValue)
      ? rawTextValue.map(value => value.trim()).filter(Boolean)
      : rawTextValue;

    const filter = {
      filterId: selectedFilterField.selectedFieldId,
      filterName: selectedFilterField.selectedFieldName,
      dropDownValue: selectedFilterField.selectedFieldDropDownValue,
      displayValue: getDisplayValue(selectedFilterField.selectedFieldDropDownValue),
      textValue,
      fieldType: selectedFilterField.selectedFieldType,
      dateValue: selectedFilterField.selectedFieldDateValue
    };

    handleFilterChange(filter);
    setSelectedFilterField(defaultSelectedField);

    // // Force remount of Select components to clear any internal state of custom Select
    setSelectKey(prev => prev + 1);
  };

  const handleFieldChange = ({ fieldValue, fieldType, index, action }) => {
    if (fieldType === TEXT_FIELD && Array.isArray(selectedFilterField.selectedFieldTextValue)) {
      setSelectedFilterField(prevState => {
        const currentValues = [...(prevState.selectedFieldTextValue || [""])];
        if (action === "add") {
          return { ...prevState, selectedFieldTextValue: [...currentValues, ""] };
        }
        if (action === "remove" && currentValues.length > 1) {
          currentValues.splice(index, 1);
          return { ...prevState, selectedFieldTextValue: currentValues };
        }
        if (typeof index === "number") {
          currentValues[index] = fieldValue.target.value;
          return { ...prevState, selectedFieldTextValue: currentValues };
        }
        return prevState;
      });
      return;
    }

    if (fieldValue) {
      const payload = {};
      if (fieldType === SELECT_FIELD) {
        payload.selectedFieldDropDownValue = fieldValue;
      } else if (fieldType === TEXT_FIELD) {
        payload.selectedFieldTextValue = fieldValue.target.value;
      } else if (fieldType === DATE_FIELD) {
        payload.selectedFieldDateValue = fieldValue;
      }
      setSelectedFilterField(prevState => ({
        ...prevState,
        ...payload
      }));
    }
  };

  // Disable "Add Filter" button if selected fields are incomplete
  const isFilterDisabled = selectedField => {
    if (!selectedField) return true;

    const dropDownValue = selectedField.selectedFieldDropDownValue;
    const hasNoDropDownValue = Array.isArray(dropDownValue)
      ? dropDownValue.length === 0
      : !dropDownValue;

    if (selectedField.selectedFieldType === SELECT_FIELD && hasNoDropDownValue) {
      return true;
    } else if (selectedField.selectedFieldType === TEXT_FIELD) {
      const textValue = selectedField.selectedFieldTextValue;
      if (Array.isArray(textValue)) {
        return !textValue.some(value => value?.trim());
      }
      return !textValue;
    } else if (
      selectedField.selectedFieldType === SELECT_DATE_FIELD &&
      (!selectedField.selectedFieldDateValue ||
        (MULTI_SELECT_FILTER_IDS.includes(selectedField.selectedFieldId) && hasNoDropDownValue))
    ) {
      return true;
    }
    return false;
  };

  // clear detailPersonSearch on unmount
  useEffect(() => {
    return () => {
      dispatch(detailPersonSearchClear());
    };
  }, []);

  const loading =
    getSiteListLoading ||
    getIdentificationNameListLoading ||
    getQualificationTitleListLoading ||
    tagsListLoading ||
    drivingLicenseCapabilityTypesListLoading;

  const hasUnaddedFilter = Boolean(selectedFilterField.selectedFieldId);

  return (
    <Spin spinning={loading} className="global-search h-full">
      <Row className="filter-list mb-4 h-full">
        <Col span={24}>
          <Title level={5}>Filter Type</Title>
          <Select
            placeholder="Search to Select"
            callback={filter => handleFilter(filter)}
            value={selectedFilterField.selectedFieldId}
            key={`contact-select-${selectKey}`}
            optionsArray={FILTER_OPTIONS}
            mode="single"
          />
        </Col>

        <Col span={24}>
          <FieldTypeGenerator
            selectedField={selectedFilterField}
            handleFieldChange={handleFieldChange}
          />
        </Col>

        <Button
          disabled={isFilterDisabled(selectedFilterField)}
          variant="primary" className="mt-2"
          onClick={() => addFilter()}
        >
          Add Filter
        </Button>

        <Col span={24} className="mt-3">
          <Alert
            type="info"
            showIcon
            className="!border-brand !bg-[#e8f6fb] [&_.ant-alert-icon]:!text-brand [&_.ant-alert-message]:!text-brand"
            message="How filters combine"
            description={
              <ul className="m-0 list-disc pl-4 text-xs">
                <li>
                  Selecting several values in one filter matches people with <b>any</b> of them (OR).
                </li>
                <li>
                  Adding separate filters matches people who meet <b>all</b> of them (AND).
                </li>
              </ul>
            }
          />
        </Col>
      </Row>

      <Row>
        <Space>
          <Button variant="outline" disabled={detailFilter.length === 0} onClick={clearDetailFilter}>
            Clear Filters
          </Button>

          <Tooltip title={hasUnaddedFilter ? "Add filter above prior to search" : null}>
            <span>
              <Button
                variant="primary"
                disabled={detailFilter.length === 0 || hasUnaddedFilter}
                onClick={searchPerson}
              >
                Search
              </Button>
            </span>
          </Tooltip>
        </Space>
      </Row>
    </Spin>
  );
};
export default Filters;
